<template>
  <!-- Single element root: Teleport alone is treated as a fragment for fallthrough attrs / listeners. -->
  <div class="tb-base-modal-root">
    <Teleport to="body">
      <Transition name="modal-backdrop">
        <div
          v-if="modelValue"
          :ref="setOverlayRef"
          data-tb-modal-overlay
          v-bind="attrs"
          :class="['fixed inset-0 flex items-center justify-center overscroll-none p-4', zIndexClass]"
        >
          <div class="absolute inset-0 bg-black/50 backdrop-blur-[2px]" />

          <Transition name="modal-dialog" appear>
            <div
              v-if="modelValue"
              ref="dialogRef"
              role="dialog"
              :aria-modal="true"
              :aria-labelledby="titleId"
              :aria-describedby="subtitle ? subtitleId : undefined"
              :class="[
                'relative z-10 w-full overflow-hidden bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]',
                sizeClass,
              ]"
            >
              <div class="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex min-w-0 flex-1 items-start gap-3">
                  <div v-if="$slots.headerIcon" class="tb-modal-header-icon">
                    <slot name="headerIcon" />
                  </div>
                  <div class="min-w-0">
                    <h2 :id="titleId" :class="titleHeadingClass">{{ title }}</h2>
                    <p v-if="subtitle" :id="subtitleId" class="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
                      {{ subtitle }}
                    </p>
                  </div>
                </div>
                <button
                  v-if="closable"
                  type="button"
                  class="shrink-0 self-start p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                  :aria-label="'Close'"
                  @click="closeDialog"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div
                ref="bodyRef"
                :class="['tb-modal-body flex-1 overflow-y-auto overscroll-contain px-6 py-5', bodyClass]"
                :tabindex="trackScrollEnd ? 0 : undefined"
                :aria-label="bodyAriaLabel"
                @scroll.passive="updateScrollEndState"
              >
                <slot />
              </div>

              <div
                v-if="$slots.footer"
                class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-white/[0.02]"
              >
                <slot name="footer" />
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, useId, watch } from 'vue'
import {
  isolateElement,
  isTopmostModal,
  registerModal,
  restoreIsolatedElement,
  unregisterModal,
} from './base-modal-stack'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()

const props = withDefaults(defineProps<{
  modelValue: boolean
  title: string
  subtitle?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  closable?: boolean
  zIndexClass?: string
  bodyClass?: string
  bodyAriaLabel?: string
  trackScrollEnd?: boolean
  scrollTrackingKey?: string | number
}>(), {
  subtitle: undefined,
  size: 'md',
  closable: true,
  zIndexClass: 'z-[120]',
  bodyClass: '',
  bodyAriaLabel: undefined,
  trackScrollEnd: false,
  scrollTrackingKey: '',
})

// Runtime event names so the compiler registers them (avoids extraneous @close warnings with Teleport root).
const emit = defineEmits(['update:modelValue', 'close', 'scroll-end-change'])

const dialogRef = ref<HTMLElement | null>(null)
const overlayRef = ref<HTMLElement | null>(null)
const bodyRef = ref<HTMLElement | null>(null)
const modalId = useId()
const titleId = `${modalId}-title`
const subtitleId = `${modalId}-subtitle`
const previouslyFocusedElement = ref<HTMLElement | null>(null)
const modalInstanceId = Symbol('base-modal')
/** Body children this overlay instance marked; each close pops and decrements global ref-count. */
const isolatedChildElements: HTMLElement[] = []
let lastScrollEndState: boolean | null = null
let bodyResizeObserver: ResizeObserver | null = null
let bodyMutationObserver: MutationObserver | null = null

const sizeClass = computed(() => {
  if (props.size === 'sm') return 'max-w-sm'
  if (props.size === 'lg') return 'max-w-2xl'
  if (props.size === 'xl') return 'max-w-5xl'
  return 'max-w-lg'
})

const titleHeadingClass = computed(() => {
  const base = 'font-semibold leading-snug text-gray-900 dark:text-white'
  if (props.size === 'lg' || props.size === 'xl') return `text-lg sm:text-xl ${base}`
  return `text-base ${base}`
})

function closeDialog(): void {
  emit('update:modelValue', false)
  emit('close')
}

function focusableElements(): HTMLElement[] {
  const root = dialogRef.value
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>([
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(','))).filter((element) => {
    const style = window.getComputedStyle(element)
    return style.display !== 'none' && style.visibility !== 'hidden'
  })
}

function focusInitialElement(): void {
  const root = dialogRef.value
  if (props.trackScrollEnd && bodyRef.value) {
    if (bodyRef.value.scrollTop > 0) bodyRef.value.scrollTop = 0
    bodyRef.value.focus()
    return
  }
  const first = focusableElements()[0]
  if (first) {
    first.focus()
    return
  }
  root?.setAttribute('tabindex', '-1')
  root?.focus()
}

function isolateBackground(): void {
  const overlay = overlayRef.value
  if (!overlay) return
  restoreBackground()
  for (const child of Array.from(document.body.children)) {
    if (!(child instanceof HTMLElement) || child === overlay || child.contains(overlay)) continue
    if (child.matches('[data-tb-modal-overlay]') || child.querySelector('[data-tb-modal-overlay]')) continue

    isolatedChildElements.push(child)
    isolateElement(child)
  }
}

function registerOpenModal(): void {
  const overlay = overlayRef.value
  if (overlay) registerModal(modalInstanceId, overlay)
}

function setOverlayRef(element: unknown): void {
  overlayRef.value = element instanceof HTMLElement ? element : null
  if (!overlayRef.value || !props.modelValue) return
  registerOpenModal()
  isolateBackground()
}

function unregisterOpenModal(): void {
  unregisterModal(modalInstanceId)
}

function isCurrentModalTopmost(): boolean {
  return isTopmostModal(modalInstanceId)
}

function restoreBackground(): void {
  while (isolatedChildElements.length > 0) {
    const child = isolatedChildElements.pop()
    if (!child) continue

    restoreIsolatedElement(child)
  }
}

function updateScrollEndState(): void {
  if (!props.trackScrollEnd || !props.modelValue) return
  const body = bodyRef.value
  if (!body) return
  const reachedEnd = body.scrollHeight - body.scrollTop <= body.clientHeight + 2
  if (lastScrollEndState === reachedEnd) return
  lastScrollEndState = reachedEnd
  emit('scroll-end-change', reachedEnd)
}

function stopScrollEndTracking(): void {
  bodyResizeObserver?.disconnect()
  bodyMutationObserver?.disconnect()
  bodyResizeObserver = null
  bodyMutationObserver = null
  lastScrollEndState = null
}

async function startScrollEndTracking(): Promise<void> {
  stopScrollEndTracking()
  if (!props.trackScrollEnd || !props.modelValue) return
  await nextTick()
  const body = bodyRef.value
  if (!body) return
  if (typeof ResizeObserver !== 'undefined') {
    bodyResizeObserver = new ResizeObserver(updateScrollEndState)
    bodyResizeObserver.observe(body)
  }
  if (typeof MutationObserver !== 'undefined') {
    bodyMutationObserver = new MutationObserver(updateScrollEndState)
    bodyMutationObserver.observe(body, { childList: true, subtree: true, characterData: true })
  }
  updateScrollEndState()
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (!props.modelValue || !isCurrentModalTopmost()) return
  if (event.key === 'Escape' || event.key === 'Tab') event.stopImmediatePropagation()
  if (event.key === 'Escape') {
    event.preventDefault()
    if (props.closable) closeDialog()
    return
  }
  if (event.key !== 'Tab') return
  const focusable = focusableElements()
  if (!focusable.length) {
    event.preventDefault()
    dialogRef.value?.focus()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      previouslyFocusedElement.value = document.activeElement instanceof HTMLElement ? document.activeElement : null
      document.addEventListener('keydown', onDocumentKeydown)
      await nextTick()
      focusInitialElement()
      await startScrollEndTracking()
    } else {
      unregisterOpenModal()
      document.removeEventListener('keydown', onDocumentKeydown)
      restoreBackground()
      const previous = previouslyFocusedElement.value
      if (previous?.isConnected && !previous.inert) {
        previous.focus()
      }
      previouslyFocusedElement.value = null
      stopScrollEndTracking()
    }
  },
  { immediate: true },
)

watch(
  () => [props.trackScrollEnd, props.scrollTrackingKey] as const,
  () => {
    if (props.modelValue) void startScrollEndTracking()
  },
)

onBeforeUnmount(() => {
  unregisterOpenModal()
  document.removeEventListener('keydown', onDocumentKeydown)
  restoreBackground()
  stopScrollEndTracking()
})
</script>

<style scoped>
/* No layout box; keeps a real component root for Vue without affecting flex/grid parents. */
.tb-base-modal-root {
  display: contents;
}

.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-dialog-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.modal-dialog-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-dialog-enter-from,
.modal-dialog-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
.modal-dialog-enter-to,
.modal-dialog-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}
</style>
